// ============================================
// AdminPanel.jsx - COMPLETE FINAL VERSION
// All affiliate/withdrawal tabs load from Firebase
// ============================================

import React, { useState } from 'react';
import { incrementBookReviewsReceived, incrementRequestedReviewsCompleted } from './firebaseService';
import { getFraudAlerts, resolveFraudAlert } from './FraudDetection';
import { NotificationTriggers } from './Notificationsystem';
import { approveCommission, processWithdrawal, getAllWithdrawals, getAllAffiliates } from './AffiliateSystem';
import { getDocs, collection, query, where, updateDoc, doc } from 'firebase/firestore';
import { db } from './firebase';
import { approveReviewServer, banUserServer } from './serverApi';
import { 
  CheckCircle, XCircle, Book, Star, ExternalLink, Crown
} from 'lucide-react';

const AdminPanel = ({ 
  currentUser,
  books,
  reviews,
  users,
  updateBook,
  updateUser,
  updateReviewStatus,
  createActivity,
  getCurrentUser,
  createNotification,
  setBooks,
  setReviews,
  getAllBooks
}) => {
  const [activeTab, setActiveTab] = useState('pending');
const [mobileTabOpen, setMobileTabOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userSearch, setUserSearch] = useState('');

  // Fraud state
  const [fraudAlerts, setFraudAlerts] = useState([]);
  const [loadingAlerts, setLoadingAlerts] = useState(false);

  const [supportTickets, setSupportTickets] = useState([]);
const [loadingTickets, setLoadingTickets] = useState(false);

  // Affiliate state
  const [allAffiliates, setAllAffiliates] = useState([]);
  const [loadingAffiliates, setLoadingAffiliates] = useState(false);

  // Withdrawal state
  const [allWithdrawals, setAllWithdrawals] = useState([]);
  const [loadingWithdrawals, setLoadingWithdrawals] = useState(false);

  // Commission state
  const [allCommissions, setAllCommissions] = useState([]);
  const [loadingCommissions, setLoadingCommissions] = useState(false);

  // ── Fraud handlers ────────────────────────
  const loadFraudAlerts = async () => {
    setLoadingAlerts(true);
    const result = await getFraudAlerts();
    if (result.success) setFraudAlerts(result.alerts);
    setLoadingAlerts(false);
  };

  const handleResolveFraudAlert = async (alertId, resolution) => {
    const labels = {
      false_positive: 'False Positive - Legitimate User',
      confirmed_fraud: 'Confirmed Fraud',
      banned: 'User Banned',
    };
    if (!window.confirm(`Mark as: ${labels[resolution]}?`)) return;
    const result = await resolveFraudAlert(alertId, currentUser.id, resolution);
    if (result.success) {
      setFraudAlerts(prev => prev.map(a =>
        a.id === alertId ? { ...a, reviewed: true, resolution } : a
      ));
      if (resolution === 'banned') {
        const fraudAlert = fraudAlerts.find(a => a.id === alertId);
        if (fraudAlert?.newUserId) {
          const banResult = await banUserServer(fraudAlert.newUserId, true);
          if (banResult.success) {
            alert('User has been banned.');
          } else {
            alert('Ban failed: ' + banResult.error);
          }
        }
      }
    }
  };


const loadSupportTickets = async () => {
  setLoadingTickets(true);
  try {
    const snapshot = await getDocs(collection(db, 'supportTickets'));
    const tickets = snapshot.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    setSupportTickets(tickets);
  } catch (e) {
    console.error('Error loading tickets:', e);
  }
  setLoadingTickets(false);
};

  // ── Affiliate handlers ────────────────────
  const loadAffiliates = async () => {
    setLoadingAffiliates(true);
    const result = await getAllAffiliates();
    if (result.success) setAllAffiliates(result.affiliates);
    setLoadingAffiliates(false);
  };

  // ── Commission handlers ───────────────────
  const loadCommissions = async () => {
    setLoadingCommissions(true);
    try {
      const snapshot = await getDocs(collection(db, 'commissions'));
      const commissions = snapshot.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setAllCommissions(commissions);
    } catch (e) {
      console.error('Error loading commissions:', e);
    }
    setLoadingCommissions(false);
  };

  const handleApproveCommission = async (commissionId) => {
    if (!window.confirm('Approve this commission? This will add funds to the affiliate balance.')) return;
    const result = await approveCommission(commissionId);
    if (result.success) {
      alert('✅ Commission approved!');
      setAllCommissions(prev => prev.map(c =>
        c.id === commissionId ? { ...c, status: 'approved' } : c
      ));
    } else {
      alert('Error: ' + result.error);
    }
  };

  // ── Withdrawal handlers ───────────────────
  const loadWithdrawals = async () => {
    setLoadingWithdrawals(true);
    const result = await getAllWithdrawals();
    if (result.success) setAllWithdrawals(result.withdrawals);
    setLoadingWithdrawals(false);
  };

  const handleProcessWithdrawal = async (withdrawalId, status) => {
    let adminNote = '';
    if (status === 'rejected') {
      adminNote = prompt('Reason for rejection:') || '';
      if (adminNote === null) return;
    }
    const confirmed = window.confirm(
      `${status === 'completed' ? '✅ Approve & Pay' : '❌ Reject'} this withdrawal?`
    );
    if (!confirmed) return;

    const result = await processWithdrawal(withdrawalId, status, adminNote);
    if (result.success) {
      alert(status === 'completed' ? '✅ Withdrawal approved and paid!' : '❌ Withdrawal rejected.');
      loadWithdrawals();
    } else {
      alert('Error: ' + result.error);
    }
  };

  // ── Book handlers ─────────────────────────
  const handleDeleteBook = async (book) => {
    const reason = prompt(`Why are you deleting "${book.title}"?\n\nThis message will be sent to the owner.`);
    if (!reason) return;
    if (!window.confirm(`⚠️ This will permanently remove the book from Browse.\n\nContinue?`)) return;

    const result = await updateBook(book.id, {
      status: 'deleted', deletedAt: new Date().toISOString(),
      deletedReason: reason
    });
    if (!result?.success) { alert('❌ Failed to delete book'); return; }

    await createNotification(book.uploadedBy, 'book_deleted', '❌ Your book was removed',
      `Your book "${book.title}" was removed by admin.\n\nReason: ${reason}`,
      { bookId: book.id, reason });

    setBooks(prev => prev.map(b =>
      b.id === book.id ? { ...b, status: 'deleted', deletedReason: reason } : b
    ));
    alert(`🗑️ "${book.title}" deleted and owner notified.`);
  };

  const handleRestoreBook = async (book) => {
    const result = await updateBook(book.id, {
      status: 'pending', deletedAt: null,
      deletedReason: null
    });
    if (!result?.success) { alert('❌ Failed to restore book'); return; }

    setBooks(prev => prev.map(b =>
      b.id === book.id ? { ...b, status: 'pending', deletedReason: null } : b
    ));
    alert(`♻️ "${book.title}" restored to Pending.`);
  };

  const calculateQualityScore = (review) => {
    let score = 5;
    if (review.wordCount >= 50) score += 2;
    if (review.wordCount >= 100) score += 1;
    if (review.reviewText.includes('character') || review.reviewText.includes('plot')) score += 1;
    if (review.reviewText.includes('recommend')) score += 1;
    return Math.min(score, 10);
  };

  const handleApproveBook = async (bookId) => {
    const book = books.find(b => b.id === bookId);
    if (!book) return;
    let ownerCoins = book.ownerCoins || 0;
    try {
      const ownerResult = await getCurrentUser(book.uploadedBy);
      if (ownerResult.success) ownerCoins = ownerResult.user.coins || 0;
    } catch (e) {}

    const result = await updateBook(bookId, {
      status: 'approved',
      approvedAt: new Date().toISOString(),
      approvedBy: currentUser.id,
      ownerCoins,
      'approval.status': 'approved'
    });

    if (result.success) {
      setBooks(prev => prev.map(b =>
        b.id === bookId ? { ...b, status: 'approved', approvedAt: new Date().toISOString(),
          ownerCoins, approval: { ...b.approval, status: 'approved' } } : b
      ));
      await NotificationTriggers.onBookApproved(book.uploadedBy, book.title);
      alert(`✅ Book "${book.title}" approved!`);
    } else {
      alert('Failed to approve book: ' + result.error);
    }
  };

  const handleRejectBookWithReason = async (bookId, reason) => {
    const book = books.find(b => b.id === bookId);
    if (!book) return;
    if (!window.confirm(`Reject "${book.title}"?\n\nReason: ${reason}`)) return;
    const result = await updateBook(bookId, {
      status: 'rejected', rejectedAt: new Date().toISOString(),
      rejectedBy: currentUser.id, rejectionReason: reason
    });
    if (result.success) {
      setBooks(prev => prev.map(b => b.id === bookId ? { ...b, status: 'rejected', rejectionReason: reason } : b));
      await NotificationTriggers.onBookRejected(book.uploadedBy, book.title, reason);
      alert(`❌ Book rejected. Author notified.`);
    }
  };

  const handleRejectBook = async (bookId) => {
    const book = books.find(b => b.id === bookId);
    if (!book) return;
    const reason = prompt('Why are you rejecting this book?');
    if (!reason) return;
    const result = await updateBook(bookId, {
      status: 'rejected', rejectedAt: new Date().toISOString(),
      rejectedBy: currentUser.id, rejectionReason: reason
    });
    if (result.success) {
      setBooks(prev => prev.map(b =>
        b.id === bookId ? { ...b, status: 'rejected', rejectionReason: reason } : b
      ));
      await NotificationTriggers.onBookRejected(book.uploadedBy, book.title, reason);
      alert(`❌ Book "${book.title}" rejected.`);
    }
  };

 // ============================================
// REPLACE handleApproveReview in AdminPanel.jsx with this
// Coin payment now happens SERVER-SIDE (admin verified on server)
// ============================================

const handleApproveReview = async (reviewId) => {
  const review = reviews.find(r => r.id === reviewId);
  if (!review) return;
  const book = books.find(b => b.id === review.bookId);
  if (!book) { alert('Book not found'); return; }

  let bookOwner = users.find(u => u.id === book.uploadedBy);
  if (!bookOwner) {
    const ownerResult = await getCurrentUser(book.uploadedBy);
    if (ownerResult.success) bookOwner = ownerResult.user;
    else { alert('Could not find book owner'); return; }
  }

  const coinsToPayReviewer = book.coinsRequired || 10;
  const qualityScore = calculateQualityScore(review);
  const bonusCoins = qualityScore >= 8 ? 5 : 0;
  const totalCoins = coinsToPayReviewer + bonusCoins;

  // 1. Mark review approved (this is fine client-side - not coins)
  await updateReviewStatus(reviewId, 'approved', qualityScore);

  // 2. SERVER pays the reviewer (verifies admin server-side, credits coins atomically)
  const paymentResult = await approveReviewServer(
    bookOwner.id,
    review.reviewerId,
    review.bookId,
    coinsToPayReviewer,
    bonusCoins
  );

  if (!paymentResult.success) {
    alert('Payment failed: ' + paymentResult.error);
    return;
  }

  // 3. Notifications (non-coin, fine client-side)
  await NotificationTriggers.onReviewApproved(review.reviewerId, book.title, coinsToPayReviewer, bonusCoins);
  await NotificationTriggers.onReviewReceived(book.uploadedBy, book.title, review.rating, review.reviewerName);
  
  // 4. Update local state
  setReviews(prev => prev.map(r => r.id === reviewId ? { ...r, status: 'approved' } : r));
  setBooks(prev => prev.map(b =>
    b.id === review.bookId ? { ...b, reviewsReceived: (b.reviewsReceived || 0) + 1 } : b
  ));

  alert(`✅ Review approved! Reviewer earned ${totalCoins} coins${bonusCoins ? ` (+${bonusCoins} bonus)` : ''}.`);
};

  const handleRejectReview = async (reviewId) => {
    const review = reviews.find(r => r.id === reviewId);
    console.log('REJECT: review object', review, 'bookId:', review?.bookId);
    if (!review) return;
    const reason = prompt('Why are you rejecting this review?');
    if (!reason) return;
    await updateReviewStatus(reviewId, 'rejected', null);
    setReviews(prev => prev.map(r =>
      r.id === reviewId ? { ...r, status: 'rejected', rejectedAt: new Date().toISOString() } : r
    ));
    // ✅ Copy returns to Browse (claimable again) since this review didn't pan out
    console.log('REJECT: about to update book', review.bookId, 'current underReview should become false');
    const bookUpdateResult = await updateBook(review.bookId, { underReview: false });
    console.log('REJECT: updateBook result', bookUpdateResult);
    if (!bookUpdateResult.success) {
      console.error('REJECT: updateBook FAILED', bookUpdateResult.error);
      alert('Failed to return book to Browse: ' + bookUpdateResult.error);
    }
    setBooks(prev => prev.map(b =>
      b.id === review.bookId ? { ...b, underReview: false } : b
    ));
    const book = books.find(b => b.id === review.bookId);
    await NotificationTriggers.onReviewRejected(review.reviewerId, book?.title || 'Unknown Book', reason);
    alert('❌ Review rejected.');
  };

  const getPendingReviews = () => reviews?.filter(r => r.status === 'pending') || [];
  const getPendingBooks = () => books?.filter(b => b.status === 'pending' || b.approval?.status === 'pending') || [];
  const getDeletedBooks = () => books?.filter(b => b.status === 'deleted') || [];
  const getPendingWithdrawals = () => allWithdrawals.filter(w => w.status === 'pending');
  const getPendingCommissions = () => allCommissions.filter(c => c.status === 'pending');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-50 to-pink-50 border-2 border-red-200 rounded-xl p-6">
        <div className="flex items-center gap-3">
          <Crown className="w-8 h-8 text-red-600" />
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Admin Dashboard</h1>
            <p className="text-xs sm:text-sm text-gray-600">Manage reviews, books, affiliates and promotions</p>

          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-md">
       {/* Mobile dropdown tab selector */}
<div className="sm:hidden border-b">
  <button
    onClick={() => setMobileTabOpen(prev => !prev)}
    className="w-full flex items-center justify-between px-4 py-3 font-semibold text-gray-800"
  >
    <span>
      {[
        { key: 'pending', icon: '✅', label: `Pending Reviews (${getPendingReviews().length})` },
        { key: 'pendingBooks', icon: '📚', label: `Pending Books (${getPendingBooks().length})` },
        { key: 'books', icon: '📖', label: `Manage Books (${books.filter(b => b.status !== 'deleted').length})` },
        { key: 'trash', icon: '🗑️', label: `Trash (${getDeletedBooks().length})` },
        { key: 'users', icon: '👥', label: `Users (${users.length})` },
        { key: 'affiliates', icon: '💰', label: `Affiliates (${allAffiliates.length})` },
        { key: 'commissions', icon: '💵', label: `Commissions (${getPendingCommissions().length})` },
        { key: 'withdrawals', icon: '🏦', label: `Withdrawals (${getPendingWithdrawals().length})` },
        { key: 'fraud', icon: '🚨', label: `Fraud (${fraudAlerts.filter(a=>!a.reviewed).length})` },
        { key: 'support', icon: '🎧', label: 'Support Tickets' },
      ].find(t => t.key === activeTab)?.icon} {[
        { key: 'pending', icon: '✅', label: `Pending Reviews (${getPendingReviews().length})` },
        { key: 'pendingBooks', icon: '📚', label: `Pending Books (${getPendingBooks().length})` },
        { key: 'books', icon: '📖', label: `Manage Books (${books.filter(b => b.status !== 'deleted').length})` },
        { key: 'trash', icon: '🗑️', label: `Trash (${getDeletedBooks().length})` },
        { key: 'users', icon: '👥', label: `Users (${users.length})` },
        { key: 'affiliates', icon: '💰', label: `Affiliates (${allAffiliates.length})` },
        { key: 'commissions', icon: '💵', label: `Commissions (${getPendingCommissions().length})` },
        { key: 'withdrawals', icon: '🏦', label: `Withdrawals (${getPendingWithdrawals().length})` },
        { key: 'fraud', icon: '🚨', label: `Fraud (${fraudAlerts.filter(a=>!a.reviewed).length})` },
        { key: 'support', icon: '🎧', label: 'Support Tickets' },
      ].find(t => t.key === activeTab)?.label}
    </span>
    <span>{mobileTabOpen ? '▲' : '▼'}</span>
  </button>
  {mobileTabOpen && (
    <div className="border-t bg-white shadow-lg z-10">
      {[
        { key: 'pending', icon: '✅', label: `Pending Reviews (${getPendingReviews().length})` },
        { key: 'pendingBooks', icon: '📚', label: `Pending Books (${getPendingBooks().length})` },
        { key: 'books', icon: '📖', label: `Manage Books (${books.filter(b => b.status !== 'deleted').length})` },
        { key: 'trash', icon: '🗑️', label: `Trash (${getDeletedBooks().length})` },
        { key: 'users', icon: '👥', label: `Users (${users.length})`, onSelect: () => setSelectedUser(null) },
        { key: 'affiliates', icon: '💰', label: `Affiliates (${allAffiliates.length})`, onSelect: loadAffiliates },
        { key: 'commissions', icon: '💵', label: `Commissions (${getPendingCommissions().length})`, onSelect: loadCommissions },
        { key: 'withdrawals', icon: '🏦', label: `Withdrawals (${getPendingWithdrawals().length})`, onSelect: loadWithdrawals },
        { key: 'fraud', icon: '🚨', label: `Fraud (${fraudAlerts.filter(a=>!a.reviewed).length})`, onSelect: loadFraudAlerts },
        { key: 'support', icon: '🎧', label: 'Support Tickets', onSelect: loadSupportTickets },
      ].map(tab => (
        <button key={tab.key}
          onClick={() => { setActiveTab(tab.key); tab.onSelect?.(); setMobileTabOpen(false); }}
          className={`w-full text-left px-4 py-3 text-sm border-b last:border-0 ${activeTab === tab.key ? 'bg-indigo-50 text-indigo-600 font-semibold' : 'text-gray-700'}`}>
          {tab.icon} {tab.label}
        </button>
      ))}
    </div>
  )}
</div>

{/* Desktop horizontal tab bar */}
<div className="hidden sm:block border-b overflow-x-auto">
  <div className="flex gap-2 px-4 min-w-max">
    {[
      { key: 'pending', label: `Pending Reviews (${getPendingReviews().length})`, icon: '✅' },
      { key: 'pendingBooks', label: `Pending Books (${getPendingBooks().length})`, icon: '📚' },
      { key: 'books', label: `Manage Books (${books.filter(b => b.status !== 'deleted').length})`, icon: '📖' },
      { key: 'trash', label: `Trash (${getDeletedBooks().length})`, icon: '🗑️' },
      { key: 'users', label: `Users (${users.length})`, icon: '👥', onSelect: () => setSelectedUser(null) },
      { key: 'affiliates', label: `Affiliates (${allAffiliates.length})`, icon: '💰', onSelect: loadAffiliates },
      { key: 'commissions', label: `Commissions (${getPendingCommissions().length})`, icon: '💵', onSelect: loadCommissions },
      { key: 'withdrawals', label: `Withdrawals (${getPendingWithdrawals().length})`, icon: '🏦', onSelect: loadWithdrawals },
      { key: 'fraud', label: `Fraud (${fraudAlerts.filter(a=>!a.reviewed).length})`, icon: '🚨', onSelect: loadFraudAlerts },
      { key: 'support', label: 'Support Tickets', icon: '🎧', onSelect: loadSupportTickets },
    ].map(tab => (
      <button key={tab.key}
        onClick={() => { setActiveTab(tab.key); tab.onSelect?.(); }}
        className={`py-4 px-3 font-medium border-b-2 transition text-sm whitespace-nowrap ${
          activeTab === tab.key
            ? tab.key === 'fraud' ? 'border-red-600 text-red-600' : 'border-indigo-600 text-indigo-600'
            : 'border-transparent text-gray-600 hover:text-gray-900'
        }`}>
        {tab.icon} {tab.label}
      </button>
    ))}
  </div>
</div>

        <div className="p-6">

          {/* ── PENDING REVIEWS ── */}
          {activeTab === 'pending' && (
            <div>
              <h2 className="text-xl font-bold text-gray-800 mb-4">Review Approvals</h2>
              {getPendingReviews().length === 0 ? (
                <div className="bg-gray-50 rounded-xl p-12 text-center">
                  <CheckCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-600 text-lg font-medium">All caught up!</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {getPendingReviews().map(review => {
                    const book = books.find(b => b.id === review.bookId);
                    return (
                      <div key={review.id} className="bg-white border-2 border-yellow-200 rounded-xl p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <h3 className="text-lg font-bold text-gray-800">{book?.title}</h3>
                            <p className="text-sm text-gray-600">by {book?.author}</p>
                            <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                              <span>👤 {review.reviewerName}</span>
                              <span>📅 {new Date(review.submittedAt?.toDate?.() || review.submittedAt).toLocaleDateString()}</span>
                              <span>💰 {book?.coinsRequired} coins</span>
                            </div>
                          </div>
                          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
  <button onClick={() => handleApproveReview(review.id)}
    className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition font-semibold flex items-center justify-center gap-2 text-sm">
    <CheckCircle className="w-4 h-4" /> Approve
  </button>
  <button onClick={() => handleRejectReview(review.id)}
    className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition font-semibold flex items-center justify-center gap-2 text-sm">
    <XCircle className="w-4 h-4" /> Reject
  </button>

                          </div>
                        </div>
                        <div className="flex items-center gap-1 mb-2">
                          {[...Array(5)].map((_, i) => (
                            <Star key={i} className={`w-5 h-5 ${i < review.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />
                          ))}
                        </div>
                        <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg mb-3">{review.reviewText}</p>
                        <div className="flex items-center gap-4 text-sm">
                          <a href={review.amazonReviewLink} target="_blank" rel="noreferrer"
                            className="text-blue-600 hover:text-blue-700 font-medium inline-flex items-center gap-1">
                            <ExternalLink className="w-4 h-4" /> View on Amazon
                          </a>
                          <span className="text-gray-500">Words: {review.wordCount || 0}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── PENDING BOOKS ── */}
          {activeTab === 'pendingBooks' && (
            <div>
              <h2 className="text-xl font-bold text-gray-800 mb-4">Book Approvals</h2>
              {getPendingBooks().length === 0 ? (
                <div className="bg-gray-50 rounded-xl p-12 text-center">
                  <CheckCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-600 text-lg font-medium">All caught up!</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {getPendingBooks().map(book => {
                    const author = users.find(u => u.id === book.uploadedBy);
                    return (
                      <div key={book.id} className="bg-white border-2 border-yellow-200 rounded-xl p-4">
                        {book.coverImage && (
                          <img src={book.coverImage} alt={book.title}
                            className="w-full h-32 object-cover rounded-lg mb-3"
                            onError={e => { e.target.style.display='none'; }} />
                        )}
                        <h3 className="font-bold text-gray-800 line-clamp-2 mb-1">{book.title}</h3>
                        <p className="text-sm text-gray-600 mb-2">by {book.author}</p>
                        <div className="flex items-center gap-2 mb-3">
                          <span className="text-xs bg-indigo-50 text-indigo-700 px-2 py-1 rounded">{book.genre}</span>
                          <span className="text-xs text-gray-600">{book.pageCount} pages</span>
                        </div>
                        <p className="text-xs text-gray-700 line-clamp-3 mb-3">{book.description}</p>
                        <p className="text-xs text-gray-500 mb-3">👤 <strong>{author?.name || 'Unknown'}</strong></p>

                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
                          <p className="text-xs font-semibold text-amber-800 mb-2">⚠️ Verify cover matches Amazon:</p>
                          <a href={book.amazonLink} target="_blank" rel="noreferrer"
                            className="w-full flex items-center justify-center gap-2 bg-amber-500 text-white px-3 py-2 rounded-lg text-sm font-semibold">
                            <ExternalLink className="w-4 h-4" /> Open Amazon Listing
                          </a>
                        </div>

                        <div className="flex flex-col gap-2">
                          <button onClick={() => handleApproveBook(book.id)}
                            className="w-full bg-green-600 text-white px-3 py-2 rounded-lg hover:bg-green-700 font-semibold text-sm flex items-center justify-center gap-1">
                            <CheckCircle className="w-4 h-4" /> Approve
                          </button>
                          <div className="grid grid-cols-2 gap-1">
                            <button onClick={() => handleRejectBookWithReason(book.id, 'Cover image does not match the Amazon listing. Please upload the correct cover.')}
                              className="bg-orange-500 text-white px-2 py-1.5 rounded-lg text-xs font-semibold">
                              Wrong Cover
                            </button>
                            <button onClick={() => handleRejectBookWithReason(book.id, 'Amazon link is invalid or does not match book details.')}
                              className="bg-orange-500 text-white px-2 py-1.5 rounded-lg text-xs font-semibold">
                              Wrong Link
                            </button>
                            <button onClick={() => handleRejectBookWithReason(book.id, 'Book details do not meet our quality standards.')}
                              className="bg-red-500 text-white px-2 py-1.5 rounded-lg text-xs font-semibold">
                              Poor Quality
                            </button>
                            <button onClick={() => handleRejectBook(book.id)}
                              className="bg-red-600 text-white px-2 py-1.5 rounded-lg text-xs font-semibold">
                              Other Reason
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── MANAGE BOOKS ── */}
          {activeTab === 'books' && (
            <div>
              <h2 className="text-xl font-bold text-gray-800 mb-4">Book Management</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {books.filter(b => b.status !== 'deleted').map(book => (
                  <div key={book.id} className="bg-white border-2 rounded-xl p-4 border-gray-200">
                    {book.coverImage && (
                      <img src={book.coverImage} alt={book.title}
                        className="w-full h-32 object-cover rounded-lg mb-3"
                        onError={e => { e.target.style.display='none'; }} />
                    )}
                    <h3 className="font-bold text-gray-800 line-clamp-2 mb-1">{book.title}</h3>
                    <p className="text-sm text-gray-600 mb-1">by {book.author}</p>
                    <p className="text-xs text-gray-500 mb-2">
                      Status: <span className={`font-semibold ${book.status === 'approved' ? 'text-green-600' : book.status === 'pending' ? 'text-yellow-600' : 'text-red-600'}`}>
                        {book.status}
                      </span>
                    </p>
                    <div className="text-xs text-gray-600 mb-2">
                      📊 {book.reviewsReceived || 0} reviews • 💰 {book.coinsRequired} coins
                    </div>
                    <div className="flex gap-1 mb-2 text-xs flex-wrap">
                      {book.amazonLink?.includes('amazon.com')
                        ? <span className="text-green-600">✅ Amazon link valid</span>
                        : <span className="text-red-600">❌ Amazon link invalid</span>}
                      {book.coverImage
                        ? <span className="text-green-600 ml-2">✅ Cover</span>
                        : <span className="text-red-600 ml-2">❌ No cover</span>}
                    </div>

                    <div className="flex flex-col gap-2 mt-2">
                      {book.status === 'pending' && (
                        <div className="flex gap-2">
                          <button onClick={() => handleApproveBook(book.id)}
                            className="flex-1 bg-green-600 text-white px-3 py-2 rounded-lg text-sm font-semibold">
                            ✅ Approve
                          </button>
                          <button onClick={() => handleRejectBook(book.id)}
                            className="flex-1 bg-red-600 text-white px-3 py-2 rounded-lg text-sm font-semibold">
                            ❌ Reject
                          </button>
                        </div>
                      )}

                      {book.status !== 'deleted' && (
                        <button onClick={() => handleDeleteBook(book)}
                          className="w-full bg-red-600 text-white py-2 rounded-lg text-sm font-semibold">
                          🗑️ Delete Book
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── TRASH ── */}
          {activeTab === 'trash' && (
            <div>
              <h2 className="text-xl font-bold text-gray-800 mb-4">Trash</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {getDeletedBooks().map(book => (
                  <div key={book.id} className="bg-white border-2 rounded-xl p-4 border-gray-200">
                    {book.coverImage && (
                      <img src={book.coverImage} alt={book.title}
                        className="w-full h-32 object-cover rounded-lg mb-3"
                        onError={e => { e.target.style.display='none'; }} />
                    )}
                    <h3 className="font-bold text-gray-800 line-clamp-2 mb-1">{book.title}</h3>
                    <p className="text-sm text-gray-600 mb-1">by {book.author}</p>
                    <p className="text-xs text-gray-500 mb-2">
                      Status: <span className="font-semibold text-red-600">
                        {book.status}
                      </span>
                    </p>
                    <div className="text-xs text-gray-600 mb-2">
                      📊 {book.reviewsReceived || 0} reviews • 💰 {book.coinsRequired} coins
                    </div>
                    <div className="flex gap-1 mb-2 text-xs flex-wrap">
                      {book.amazonLink?.includes('amazon.com')
                        ? <span className="text-green-600">✅ Amazon link valid</span>
                        : <span className="text-red-600">❌ Amazon link invalid</span>}
                      {book.coverImage
                        ? <span className="text-green-600 ml-2">✅ Cover</span>
                        : <span className="text-red-600 ml-2">❌ No cover</span>}
                    </div>

                    <div className="bg-red-50 border border-red-200 rounded-lg p-2 mb-2 text-xs text-red-700">
                      <p><strong>Reason:</strong> {book.deletedReason || 'No reason recorded'}</p>
                      <p><strong>Deleted:</strong> {book.deletedAt ? new Date(book.deletedAt).toLocaleString() : 'Unknown'}</p>
                    </div>

                    <div className="flex flex-col gap-2 mt-2">
                      <button onClick={() => handleRestoreBook(book)}
                        className="w-full bg-green-600 text-white py-2 rounded-lg text-sm font-semibold">
                        ♻️ Restore
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              {getDeletedBooks().length === 0 && (
                <p className="text-sm text-gray-500">No deleted books.</p>
              )}
            </div>
          )}

          {/* ── USERS (view-only) ── */}
          {activeTab === 'users' && (
            <div>
              {!selectedUser ? (
                <>
                  <h2 className="text-xl font-bold text-gray-800 mb-4">Users</h2>
                  <input
                    type="text"
                    value={userSearch}
                    onChange={e => setUserSearch(e.target.value)}
                    placeholder="Search by name or email..."
                    className="w-full px-3 py-2 border rounded-lg mb-4"
                  />
                  {(() => {
                    const q = userSearch.trim().toLowerCase();
                    const filteredUsers = users.filter(u =>
                      !q || (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q)
                    );
                    if (filteredUsers.length === 0) {
                      return <p className="text-sm text-gray-500">No users match your search.</p>;
                    }
                    return (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {filteredUsers.map(u => (
                          <div key={u.id} className="bg-white border-2 rounded-xl p-4 border-gray-200">
                            <div className="flex items-center justify-between mb-1">
                              <h3 className="font-bold text-gray-800 line-clamp-1">{u.name || 'Unnamed'}</h3>
                              {u.isBanned && (
                                <span className="text-xs bg-red-600 text-white px-2 py-0.5 rounded-full font-semibold">BANNED</span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 mb-2 truncate">{u.email}</p>
                            <div className="text-xs text-gray-600 mb-2">
                              💰 {u.coins || 0} coins
                            </div>
                            <p className="text-xs text-gray-500 mb-3">
                              Review Plan: <span className={`font-semibold ${u.reviewSubscription?.active ? 'text-green-600' : 'text-gray-500'}`}>
                                {u.reviewSubscription?.active ? `${(u.reviewSubscription.plan || '').toUpperCase()} — Active` : 'Inactive'}
                              </span>
                            </p>
                            <button onClick={() => setSelectedUser(u)}
                              className="w-full bg-indigo-600 text-white py-2 rounded-lg text-sm font-semibold">
                              View Activity
                            </button>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </>
              ) : (
                <>
                  <button onClick={() => setSelectedUser(null)} className="text-indigo-600 mb-4 text-sm font-medium">
                    ← Back to users
                  </button>

                  <div className="bg-white border-2 border-gray-200 rounded-xl p-4 mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <h2 className="text-xl font-bold text-gray-800">{selectedUser.name || 'Unnamed'}</h2>
                      {selectedUser.isBanned && (
                        <span className="text-xs bg-red-600 text-white px-2 py-0.5 rounded-full font-semibold">BANNED</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mb-3">{selectedUser.email}</p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
                      <div className="bg-gray-50 rounded-lg p-2">
                        <p className="text-lg font-bold text-gray-800">{selectedUser.coins || 0}</p>
                        <p className="text-xs text-gray-500">Coins</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-2">
                        <p className="text-lg font-bold text-gray-800">{selectedUser.activitiesUsed || 0}/3</p>
                        <p className="text-xs text-gray-500">Trial Activities</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-2">
                        <p className="text-lg font-bold text-gray-800">{selectedUser.reviewsSubmitted || 0}</p>
                        <p className="text-xs text-gray-500">Reviews Submitted</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-2">
                        <p className={`text-sm font-bold ${selectedUser.reviewSubscription?.active ? 'text-green-600' : 'text-gray-500'}`}>
                          {selectedUser.reviewSubscription?.active ? ((selectedUser.reviewSubscription.plan || '').toUpperCase() || 'ACTIVE') : 'Inactive'}
                        </p>
                        <p className="text-xs text-gray-500">Review Plan</p>
                      </div>
                    </div>
                    <div className="text-xs text-gray-500 mt-3 space-y-1">
                      {selectedUser.reviewSubscription?.active && selectedUser.reviewSubscription?.expiresAt && (
                        <p>Plan expires: {new Date(selectedUser.reviewSubscription.expiresAt).toLocaleDateString()}</p>
                      )}
                      <p>Joined: {selectedUser.createdAt?.toDate ? selectedUser.createdAt.toDate().toLocaleDateString() : (selectedUser.createdAt ? new Date(selectedUser.createdAt).toLocaleDateString() : 'Unknown')}</p>
                    </div>
                  </div>

                  <h3 className="font-bold text-gray-800 mb-3">Submitted Reviews</h3>
                  {(() => {
                    const userReviews = reviews.filter(r => r.reviewerId === selectedUser.id);
                    if (userReviews.length === 0) {
                      return <p className="text-sm text-gray-500">No reviews submitted yet.</p>;
                    }
                    return (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {userReviews.map(r => {
                          const book = books.find(b => b.id === r.bookId);
                          return (
                            <div key={r.id} className="bg-white border-2 rounded-xl p-4 border-gray-200">
                              <h4 className="font-bold text-gray-800 line-clamp-2 mb-1">{book?.title || 'Unknown book'}</h4>
                              <div className="flex items-center gap-1 mb-2">
                                {[...Array(5)].map((_, i) => (
                                  <Star key={i} className={`w-4 h-4 ${i < (r.rating || 0) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />
                                ))}
                              </div>
                              <p className="text-xs text-gray-500 mb-2">
                                Status: <span className={`font-semibold ${r.status === 'approved' ? 'text-green-600' : r.status === 'pending' ? 'text-yellow-600' : 'text-red-600'}`}>
                                  {r.status}
                                </span>
                              </p>
                              {r.reviewText && (
                                <p className="text-xs text-gray-600 mb-2 line-clamp-3">{r.reviewText}</p>
                              )}
                              <p className="text-xs text-gray-500 mb-2">
                                Submitted: {r.submittedAt?.toDate ? r.submittedAt.toDate().toLocaleDateString() : (r.submittedAt ? new Date(r.submittedAt).toLocaleDateString() : 'Unknown')}
                              </p>
                              {r.amazonReviewLink && (
                                <a href={r.amazonReviewLink} target="_blank" rel="noreferrer"
                                  className="text-xs text-blue-600 inline-flex items-center hover:text-blue-700">
                                  <ExternalLink className="w-3 h-3 mr-1" /> View on Amazon
                                </a>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </>
              )}
            </div>
          )}

          {/* ── AFFILIATES ── */}
          {activeTab === 'affiliates' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <div><h2 className="text-lg sm:text-xl font-bold text-gray-800">
                  Affiliates</h2>
                  <p className="text-sm text-gray-500">{allAffiliates.length} total</p>
                </div>
                <button onClick={loadAffiliates} disabled={loadingAffiliates}
                  className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium">
                  {loadingAffiliates ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> : '🔄'} Refresh
                </button>
              </div>

              {allAffiliates.length === 0 ? (
                <div className="bg-gray-50 rounded-xl p-12 text-center">
                  <p className="text-gray-600 text-lg font-medium">No affiliates yet</p>
                  <button onClick={loadAffiliates} className="mt-4 bg-indigo-600 text-white px-6 py-2 rounded-lg text-sm">Load</button>
                </div>
              ) : (
                <div className="space-y-3">
                  {allAffiliates.map(aff => (
                    <div key={aff.id} className="bg-white border border-gray-200 rounded-xl p-4">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div>
                          <p className="font-bold text-gray-800">{aff.name}</p>
                          <p className="text-sm text-gray-500">{aff.email}</p>
                          <p className="text-xs text-gray-400 font-mono mt-1">Code: {aff.referralCode}</p>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
                          {[
                            { label: 'Clicks', val: aff.clicks || 0, color: 'blue' },
                            { label: 'Referrals', val: aff.totalReferrals || 0, color: 'green' },
                            { label: 'Pending $', val: `$${(aff.pendingEarnings || 0).toFixed(2)}`, color: 'yellow' },
                            { label: 'Earned $', val: `$${(aff.totalEarnings || 0).toFixed(2)}`, color: 'purple' },
                          ].map(s => (
                            <div key={s.label} className={`bg-${s.color}-50 px-3 py-2 rounded-lg`}>
                              <p className="text-xs text-gray-500">{s.label}</p>
                              <p className={`font-bold text-${s.color}-600`}>{s.val}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── COMMISSIONS ── */}
          {activeTab === 'commissions' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-lg sm:text-xl font-bold text-gray-800">
                    Commissions</h2>
                  <p className="text-sm text-gray-500">{getPendingCommissions().length} pending approval</p>
                </div>
                <button onClick={loadCommissions} disabled={loadingCommissions}
                  className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium">
                  {loadingCommissions ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> : '🔄'} Refresh
                </button>
              </div>

              {allCommissions.length === 0 ? (
                <div className="bg-gray-50 rounded-xl p-12 text-center">
                  <p className="text-gray-600 text-lg font-medium">No commissions yet</p>
                  <button onClick={loadCommissions} className="mt-4 bg-indigo-600 text-white px-6 py-2 rounded-lg text-sm">Load</button>
                </div>
              ) : (
                <div className="space-y-3">
                  {allCommissions.map(c => (
                    <div key={c.id} className={`border-2 rounded-xl p-4 ${c.status === 'pending' ? 'border-yellow-200 bg-yellow-50' : 'border-green-200 bg-green-50'}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-bold text-gray-800">${c.amount?.toFixed(2)}</p>
                          <p className="text-sm text-gray-600">{c.subscriptionType?.replace('_', ' ').toUpperCase()}</p>
                          <p className="text-xs text-gray-500">Code: {c.affiliateCode} • {new Date(c.createdAt).toLocaleDateString()}</p>
                        </div>
                        <div className="text-right">
                          <span className={`inline-block px-2 py-1 rounded-full text-xs font-bold mb-2 ${c.status === 'approved' ? 'bg-green-200 text-green-800' : 'bg-yellow-200 text-yellow-800'}`}>
                            {c.status?.toUpperCase()}
                          </span>
                          {c.status === 'pending' && (
                            <div>
                              <button onClick={() => handleApproveCommission(c.id)}
                                className="block w-full bg-green-600 text-white px-4 py-1.5 rounded-lg text-sm font-medium">
                                ✅ Approve
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── WITHDRAWALS ── */}
          {activeTab === 'withdrawals' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-lg sm:text-xl font-bold text-gray-800">
                    Withdrawal Requests</h2>
                  <p className="text-sm text-gray-500">{getPendingWithdrawals().length} pending</p>
                </div>
                <button onClick={loadWithdrawals} disabled={loadingWithdrawals}
                  className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium">
                  {loadingWithdrawals ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> : '🔄'} Refresh
                </button>
              </div>

              {allWithdrawals.length === 0 ? (
                <div className="bg-gray-50 rounded-xl p-12 text-center">
                  <p className="text-gray-600 text-lg font-medium">No withdrawal requests</p>
                  <button onClick={loadWithdrawals} className="mt-4 bg-indigo-600 text-white px-6 py-2 rounded-lg text-sm">Load</button>
                </div>
              ) : (
                <div className="space-y-4">
                  {allWithdrawals.map(w => (
                    <div key={w.id} className={`border-2 rounded-xl p-4 ${
                      w.status === 'pending' ? 'border-yellow-200 bg-yellow-50' :
                      w.status === 'completed' ? 'border-green-200 bg-green-50' :
                      'border-red-200 bg-red-50'
                    }`}>
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-bold text-gray-800 text-lg">${w.amount?.toFixed(2)}</p>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                              w.status === 'completed' ? 'bg-green-200 text-green-800' :
                              w.status === 'rejected' ? 'bg-red-200 text-red-800' :
                              'bg-yellow-200 text-yellow-800'
                            }`}>{w.status?.toUpperCase()}</span>
                          </div>
                          <p className="text-sm text-gray-700"><strong>{w.affiliateName}</strong> ({w.affiliateEmail})</p>
                          <p className="text-xs text-gray-500">Requested: {new Date(w.requestedAt).toLocaleDateString()}</p>
                        </div>
                      </div>

                      <div className="bg-white bg-opacity-70 rounded-lg p-3 mb-3 text-sm space-y-1">
                        <p><strong>Method:</strong> {w.paymentMethod}</p>
                        <p className="break-all"><strong>Details:</strong> {w.paymentDetails}</p>
                        {w.adminNote && <p><strong>Note:</strong> {w.adminNote}</p>}
                      </div>

                      {w.status === 'pending' && (
                        <div className="flex flex-col sm:flex-row gap-2 mt-2">
  <button onClick={() => handleProcessWithdrawal(w.withdrawalId || w.id, 'completed')}
    className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition text-sm font-medium text-center">
    ✅ Approve & Pay
  </button>
  <button onClick={() => handleProcessWithdrawal(w.withdrawalId || w.id, 'rejected')}
    className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition text-sm font-medium text-center">
    ❌ Reject
  </button>
</div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── FRAUD ── */}
          {activeTab === 'fraud' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-lg sm:text-xl font-bold text-gray-800">Fraud Detection</h2>
                  <p className="text-sm text-gray-500">{fraudAlerts.filter(a => !a.reviewed).length} unreviewed</p>
                </div>
                <button onClick={loadFraudAlerts} disabled={loadingAlerts}
                  className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm">
                  {loadingAlerts ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> : '🔄'} Refresh
                </button>
              </div>

              {fraudAlerts.length === 0 ? (
                <div className="bg-gray-50 rounded-xl p-12 text-center">
                  <p className="text-5xl mb-4">🛡️</p>
                  <p className="text-gray-600 text-lg font-medium">No fraud alerts yet</p>
                  <button onClick={loadFraudAlerts} className="mt-4 bg-indigo-600 text-white px-6 py-2 rounded-lg text-sm">Load Alerts</button>
                </div>
              ) : (
                <div className="space-y-4">
                  {fraudAlerts.filter(a => !a.reviewed).map(alert => (
                    <div key={alert.id} className={`border-2 rounded-xl p-5 ${
                      alert.riskScore >= 90 ? 'bg-red-50 border-red-300' :
                      alert.riskScore >= 70 ? 'bg-orange-50 border-orange-300' :
                      'bg-yellow-50 border-yellow-300'
                    }`}>
                      <p className="font-bold text-lg mb-1">
                        {alert.riskScore >= 90 ? '🔴 BLOCKED' : alert.riskScore >= 70 ? '🟠 HIGH RISK' : '🟡 SUSPICIOUS'}
                        <span className="ml-2 text-sm font-normal">Score: {alert.riskScore}/100</span>
                      </p>
                      <div className="bg-white bg-opacity-70 rounded-lg p-3 mb-3 text-sm grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <div><p className="text-xs text-gray-500">New User ID</p><p className="font-mono text-xs truncate">{alert.newUserId}</p></div>
                        {alert.matchedUserId && <div><p className="text-xs text-gray-500">Matched User</p><p className="font-mono text-xs truncate">{alert.matchedUserId}</p></div>}
                        <div><p className="text-xs text-gray-500">IP</p><p className="font-mono text-xs">{alert.ipAddress}</p></div>
                        <div><p className="text-xs text-gray-500">Device ID</p><p className="font-mono text-xs truncate">{alert.deviceId}</p></div>
                      </div>
                      {alert.matchedSignals?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-3">
                          {alert.matchedSignals.map(s => (
                            <span key={s} className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs font-mono">{s}</span>
                          ))}
                        </div>
                      )}
                      <div className="flex flex-col sm:flex-row gap-2">
                        <button onClick={() => handleResolveFraudAlert(alert.id, 'false_positive')}
                          className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium">✅ Legitimate</button>
                        <button onClick={() => handleResolveFraudAlert(alert.id, 'confirmed_fraud')}
                          className="flex-1 bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium">⚠️ Flag Fraud</button>
                        <button onClick={() => handleResolveFraudAlert(alert.id, 'banned')}
                          className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium">🚫 Ban User</button>
                      </div>
                    </div>
                  ))}
                  {fraudAlerts.filter(a => a.reviewed).length > 0 && (
                    <div className="border border-gray-200 rounded-xl p-4">
                      <p className="text-sm font-medium text-gray-600 mb-3">✅ {fraudAlerts.filter(a => a.reviewed).length} Reviewed</p>
                      {fraudAlerts.filter(a => a.reviewed).slice(0, 5).map(alert => (
                        <div key={alert.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0 text-sm">
                          <span className="font-mono text-xs truncate max-w-[120px]">{alert.newUserId}</span>
                          <span className="text-gray-500">Score: {alert.riskScore}</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs ${
                            alert.resolution === 'false_positive' ? 'bg-green-100 text-green-700' :
                            alert.resolution === 'banned' ? 'bg-red-100 text-red-700' :
                            'bg-orange-100 text-orange-700'
                          }`}>{alert.resolution?.replace('_', ' ').toUpperCase()}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}


{/* ── SUPPORT TICKETS ── */}
{activeTab === 'support' && (
  <div>
    <div className="flex items-center justify-between mb-6">
      <div>
        <h2 className="text-lg sm:text-xl font-bold text-gray-800">Support Tickets</h2>
        <p className="text-sm text-gray-500">{supportTickets.filter(t => t.status === 'open').length} open tickets</p>
      </div>
      <button onClick={loadSupportTickets} disabled={loadingTickets}
        className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium">
        {loadingTickets ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> : '🔄'} Refresh
      </button>
    </div>

    {supportTickets.length === 0 ? (
      <div className="bg-gray-50 rounded-xl p-12 text-center">
        <p className="text-5xl mb-4">🎧</p>
        <p className="text-gray-600 text-lg font-medium">No support tickets yet</p>
        <button onClick={loadSupportTickets} className="mt-4 bg-indigo-600 text-white px-6 py-2 rounded-lg text-sm">
          Load Tickets
        </button>
      </div>
    ) : (
      <div className="space-y-4">
        {supportTickets.map(ticket => (
          <div key={ticket.id} className={`border-2 rounded-xl p-4 ${
            ticket.status === 'open' ? 'border-yellow-200 bg-yellow-50' :
            ticket.status === 'resolved' ? 'border-green-200 bg-green-50' :
            'border-gray-200 bg-gray-50'
          }`}>
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <p className="font-bold text-gray-800">{ticket.subject || ticket.category || 'Support Request'}</p>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                    ticket.status === 'open' ? 'bg-yellow-200 text-yellow-800' :
                    ticket.status === 'resolved' ? 'bg-green-200 text-green-800' :
                    'bg-gray-200 text-gray-800'
                  }`}>{ticket.status?.toUpperCase()}</span>
                </div>
                <p className="text-sm text-gray-600">From: <strong>{ticket.userName || ticket.userEmail}</strong></p>
                <p className="text-xs text-gray-400">{new Date(ticket.createdAt).toLocaleString()}</p>
              </div>
            </div>

            <div className="bg-white rounded-lg p-3 mb-3 text-sm text-gray-700">
              {ticket.message || ticket.description}
            </div>

            {/* Admin Reply */}
            <div className="space-y-2">
              <textarea
                placeholder="Type your reply..."
                className="w-full px-3 py-2 border rounded-lg text-sm"
                rows="2"
                id={`reply-${ticket.id}`}
              />
              <div className="flex gap-2">
                <button
                  onClick={async () => {
                    const reply = document.getElementById(`reply-${ticket.id}`).value;
                    if (!reply.trim()) return alert('Please type a reply');
                    await updateDoc(doc(db, 'supportTickets', ticket.id), {
                      adminReply: reply,
                      status: 'resolved',
                      resolvedAt: new Date().toISOString(),
                      resolvedBy: currentUser.id
                    });
                    setSupportTickets(prev => prev.map(t =>
                      t.id === ticket.id ? { ...t, adminReply: reply, status: 'resolved' } : t
                    ));
                    alert('✅ Reply sent and ticket resolved!');
                  }}
                  className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-700"
                >
                  ✅ Reply & Resolve
                </button>
                <button
                  onClick={async () => {
                    await updateDoc(doc(db, 'supportTickets', ticket.id), {
                      status: 'closed',
                      closedAt: new Date().toISOString()
                    });
                    setSupportTickets(prev => prev.map(t =>
                      t.id === ticket.id ? { ...t, status: 'closed' } : t
                    ));
                  }}
                  className="bg-gray-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-700"
                >
                  Close
                </button>
              </div>
              {ticket.adminReply && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                  <p className="font-medium text-blue-800 mb-1">Your previous reply:</p>
                  <p className="text-blue-700">{ticket.adminReply}</p>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
)}

        </div>
      </div>
    </div>
  );
};

export default AdminPanel;